import { Injectable } from "@angular/core";
// BFX
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { StrategyLayoutData } from "app/shared/d3/strategy-layout/StrategyLayoutData";
import { StrategyData } from "app/shared/d3/strategy-layout/StrategyData";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEDataModel } from "../data/models/oe-data.model";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEStrategyRefModel } from "../data/models/oe-strategy.model";
import { OESettingsService } from "./basic/oe-settings.service";

@Injectable()
export class OEStrategyLayoutService {
  constructor(private getter: OEModelGetterService, private settings: OESettingsService) {}

  public getStrategyNodeLabel(refId: string) {
    try {
      let model = this.getter.get(refId);

      if (model) {
        return this.getStrategyNodeLabelByModel(model);
      }
    } catch (e) {
      // logger.out.info('failed to get updated strategy label');
    }
  }

  private getStrategyNodeLabelByModel(data: OEDataModel): string {
    let label = "";
    // error handling for a weird bug on Modify when orderType is null on firs load (happens using VPN)
    if (data != null && data.orderType == null && data.refId != null) {
      data = this.getter.get(data.refId);
    }

    //
    if (
      data == null ||
      data.orderType == null ||
      data.orderType.value == null ||
      !CubiqTS.RequestOrderType.getAllElements().includes(data.orderType.value)
    ) {
      return " Error"; // space before 'Error' text is important!
    }

    if (CubiqTS.RequestOrderType.CALL === data.orderType.value) {
      let manualExecCallName = this.settings.getManualExecutionCallName() + " ";
      let orderTypeLabel =
        data.executionMode == CubiqTS.ExecutionType.MANUAL ? manualExecCallName : "CALL ";
      label += orderTypeLabel;
    } else if (CubiqTS.OrderIntent.BUY === data.clientIntent) {
      label += "B ";
    } else if (CubiqTS.OrderIntent.SELL === data.clientIntent) {
      label += "S ";
    } else {
      logger.out.info("Failed to parse client intent");
    }

    if (CubiqTS.RequestOrderType.TP === data.orderType.value) {
      label += "TP ";
    } else if (
      CubiqTS.RequestOrderType.SL_ATRATE === data.orderType.value ||
      CubiqTS.RequestOrderType.SL_BID === data.orderType.value ||
      CubiqTS.RequestOrderType.SL_ASK === data.orderType.value
    ) {
      label += "SL ";
    } else if (
      CubiqTS.RequestOrderType.TSL == data.orderType.value ||
      CubiqTS.RequestOrderType.TSL_BID === data.orderType.value ||
      CubiqTS.RequestOrderType.TSL_ASK === data.orderType.value
    ) {
      label += "TSL ";
    } else if (CubiqTS.RequestOrderType.MARKET === data.orderType.value) {
      label += "MKT ";
      if (typeof data.ccyPair === "string" && data.ccyPair.length === 6) {
        label += data.ccyPair + " ";
      }
    }

    if (CubiqTS.RequestOrderType.MARKET !== data.orderType.value && data.requestPrice) {
      const pair = data.ccyPairModel;
      const isInvert = pair.base + pair.nonBase != data.ccyPair;
      const precision = isInvert ? pair.limitEntrySpotInvertDecimals : pair.limitEntrySpotDecimals;
      label += data.requestPrice.toFixed(precision);
    }

    return label;
  }

  public getStrategyLayout(refId: string): StrategyLayoutData {
    let orderMap = new Map(); // key:x#y# value:d
    let childLinks = []; // y ... y + 1
    let siblingLinks = []; // x ... x + 1
    let visitorPaths = []; // set
    let width = 0;
    let height = 0;
    let maxX = 0;
    let maxY = 0;

    // ARGH
    let _oeStrategyLayout = this;
    let _oeModel = this.getter;

    function adaptDataModel(this: void, data: OEDataModel, d: StrategyData) {
      d.id = data.refId;

      d.label = _oeStrategyLayout.getStrategyNodeLabelByModel(data);

      if (Array.isArray(data.strategy.children)) {
        d.childRelationshipTypes = data.strategy.children.map((relationship) => {
          return relationship.type;
        });
      }

      if (Array.isArray(data.strategy.siblings) && data.strategy.siblings.length >= 1) {
        // svg can only render one sibling per node
        d.siblingRelationshipType = data.strategy.siblings[0].type;
      }
    }

    function handleOrder(
      this: void,
      order: OEDataModel,
      d: StrategyData,
      x,
      y,
      oX,
      oY,
      childIndex?,
    ) {
      if (order instanceof OEDataModel) {
        adaptDataModel(order, d);
      }

      d.x = x;
      d.y = y;

      if (oX === undefined || oX === null) {
        oX = -1;
      }

      if (oY === undefined || oY === null) {
        oY = -1;
      }

      // track for translation
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);

      // check for link to sibling
      if (childIndex === undefined) {
        let siblingLink = handleBacklink("x" + oX + "y" + oY, d, "SIBLING");
        if (siblingLink !== null) {
          siblingLinks.push(siblingLink);
        }
      } else {
        // check for link to parent
        let childLink = handleBacklink("x" + oX + "y" + oY, d, "CHILD", childIndex);
        if (childLink !== null) {
          childLinks.push(childLink);
        }
      }

      // track for d3 data() & scaling (can't scale yet!)
      if (orderMap.has("x" + x + "y" + y)) {
        logger.out.info("Node collision in strategy layout data generation!");
        throw new Error("Node Collision");
      }

      orderMap.set("x" + x + "y" + y, d);
    }

    function handleBacklink(this: void, key, d: StrategyData, type, index?) {
      let source: StrategyData = orderMap.get(key);
      if (source !== undefined && source !== null) {
        d.visitorPath = source.visitorPath.slice();

        let expiryIndex = source.visitorPath[source.visitorPath.length - 1].expiryIndex;

        if (index === undefined) {
          d.visitorPath.push({
            type: type,
            /* pathType - refactor or add isSibling/isChild to enum? */
            orderRelationshipType: source.siblingRelationshipType,
            expiryIndex: expiryIndex,
            sourceId: source.id,
            rootId: d.visitorPath[0].rootId,
            targetId: d.id,
          });
        } else {
          d.childIndex = index;

          if (source.childRelationshipTypes[index] === CubiqTS.OrderRelationshipType.ON_TIMEOUT) {
            expiryIndex++;
          }

          let pathElement = {
            type: type,
            index: index,
            orderRelationshipType: source.childRelationshipTypes[index],
            expiryIndex: expiryIndex,
            rootId: d.visitorPath[0].rootId,
            sourceId: source.id,
            targetId: d.id,
          };
          d.visitorPath.push(pathElement);
        }
        return { source: source, target: d };
      } else {
        // track for tree
        d.visitorPath = [
          {
            type: "ROOT",
            orderRelationshipType: CubiqTS.OrderRelationshipType.NONE,
            expiryIndex: 0,
            rootId: d.id,
            sourceId: d.id,
            targetId: d.id,
          },
        ];
      }

      return null;
    }

    function traverseChildOrders(
      this: void,
      childRelationships: OEStrategyRefModel[],
      nextX,
      nextY,
      oX,
      oY,
    ) {
      let maxNextX = nextX;
      let i = 0;

      while (i < childRelationships.length) {
        let currentOrder = _oeModel.get(childRelationships[i].refId);
        let currentNextX = traverseOrder(
          currentOrder,
          new StrategyData(),
          maxNextX,
          nextY,
          oX,
          oY,
          i,
        );
        maxNextX = Math.max(maxNextX, currentNextX);
        /**
         * NOTE: the commented code below is used in order to solve https://www.bcdfx.com/ijira/browse/OMS-21611 in order details
         * however it causes problems on order entry, given ROOT->IF_DONE->OCO structure if you attempt to add another ROOT-IF_DONE (child)
         * the new node is not rendered and the rendering plugin crashes.
         * For now not using the fix code on order entry seems to solve the order entry problem and avoids the OMS-21611 bug.
         */
        // if (Array.isArray(currentOrder.strategy.siblings) && currentOrder.strategy.siblings.length > 0 &&
        // 	CubiqTS.OrderRelationshipType.OCO == currentOrder.strategy.siblings[0].type) {
        // 	// skip second OCO child in model.
        // 	i++;
        // }
        i++;
      }

      return maxNextX;
    }

    function traverseSiblingOrder(
      this: void,
      data: OEDataModel,
      d: StrategyData,
      x,
      y,
      oX,
      oY,
    ): number {
      handleOrder(data, d, x, y, oX, oY);

      let nextX = x + 1;
      let nextY = y + 1;

      // check if sibling has any children
      if (Array.isArray(data.strategy.children) && data.strategy.children.length > 0) {
        let currentNextX = traverseChildOrders(data.strategy.children, x, nextY, x, y);
        nextX = Math.max(nextX, currentNextX);
      } else {
        // edge detected
        visitorPaths.push(d.visitorPath);
      }
      return nextX;
    }

    function traverseOrder(
      this: void,
      data: OEDataModel,
      d: StrategyData,
      x,
      y,
      oX?,
      oY?,
      childIndex?,
    ) {
      handleOrder(data, d, x, y, oX, oY, childIndex);

      let nextX = x + 1;
      let nextY = y + 1;

      // OP traverse children
      if (Array.isArray(data.strategy.children) && data.strategy.children.length > 0) {
        nextX = traverseChildOrders(data.strategy.children, x, nextY, x, y);
      }
      // OP traverse siblings
      if (Array.isArray(data.strategy.siblings) && data.strategy.siblings.length > 0) {
        nextX = traverseSiblingOrder(
          _oeModel.get(data.strategy.siblings[0].refId),
          new StrategyData(),
          nextX,
          y,
          x,
          y,
        );
      }

      if (
        (d.childRelationshipTypes !== Object(d.childRelationshipTypes) ||
          d.childRelationshipTypes.length === 0) &&
        d.siblingRelationshipType === null
      ) {
        // edge detected
        visitorPaths.push(d.visitorPath);
      }
      return nextX;
    }

    let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
    /* let model = this._oeModel.get(refId);
		let rootRefId;

		if (model.strategy == Object(model.strategy) && 
			model.strategy.rootRefId) {
				rootRefId = model.strategy.rootRefId;
		} else {
			rootRefId = refId;
		} */

    traverseOrder(this.getter.get(rootRefId), new StrategyData(), 0, 0);

    return new StrategyLayoutData(
      Array.from(orderMap.values()),
      childLinks,
      siblingLinks,
      visitorPaths,
      width,
      height,
      maxX,
      maxY,
    );
  }
}
